<template>
  <div class="base-page">
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <h1 class="mb-4">{{ currentContent.title }}</h1>
          
          <!-- Loading State -->
          <div v-if="loading" class="loading-container">
            <div class="loading-spinner"></div>
            <p>{{ currentContent.loading }}</p>
          </div>
          
          <!-- Tabs Section -->
          <div v-else class="tabs-container">
            <!-- Tab Navigation -->
            <ul class="nav nav-tabs" role="tablist">
              <li class="nav-item" v-for="(tab, index) in tabs" :key="index">
                <button
                  class="nav-link"
                  :class="{ active: activeTab === index }"
                  @click="activeTab = index"
                  type="button"
                >
                  {{ tab.title }}
                </button>
              </li>
            </ul>

            <!-- Tab Content -->
            <div class="tab-content">
              <!-- Codification Matrix Tab -->
              <div v-if="activeTab === 0" class="tab-pane" style="display: block !important;">
                <div class="table-section" v-if="content[language] && content[language].codificationMatrix">
                  <h3>{{ content[language].codificationMatrix.title }}</h3>
                  <p class="section-description">{{ content[language].codificationMatrix.description }}</p>
                  
                  <div v-if="Object.keys(codificationData).length === 0" class="no-data-message">
                    <p>{{ content[language].noData || 'No data available' }}</p>
                  </div>
                  
                  <div v-else>
                    <div v-for="(trait, traitName) in codificationData" :key="traitName" class="trait-section">
                      <div class="trait-header" @click="toggleTrait(`cod_${traitName}`)">
                        <h4 class="trait-title">{{ traitName }}</h4>
                        <div class="collapse-icon" :class="{ 'expanded': expandedTraits[`cod_${traitName}`] }">
                          <i class="icon">▼</i>
                        </div>
                      </div>
                      
                      <div class="trait-content" v-show="expandedTraits[`cod_${traitName}`]">
                        <div class="table-responsive">
                          <table class="table table-striped">
                            <thead>
                              <tr>
                                <th>{{ content[language].codificationMatrix.table.score }}</th>
                                <th>{{ content[language].codificationMatrix.table.level }}</th>
                                <th>{{ content[language].codificationMatrix.table.behavioral }}</th>
                                <th>{{ content[language].codificationMatrix.table.ttp }}</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(value, score) in trait" :key="score">
                                <td><strong>{{ score }}</strong></td>
                                <td>{{ value.level }}</td>
                                <td>{{ value.behavioral_description }}</td>
                                <td>{{ value.ttp_indicators }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="no-data-message">
                  <p>Codification Matrix data not available</p>
                </div>
              </div>

              <!-- Interpretation Table Tab -->
              <div v-if="activeTab === 1" class="tab-pane" style="display: block !important;">
                <div class="table-section" v-if="content[language] && content[language].interpretationTable">
                  <h3>{{ content[language].interpretationTable.title }}</h3>
                  <p class="section-description">{{ content[language].interpretationTable.description }}</p>
                  
                  <div v-if="Object.keys(interpretationData).length === 0" class="no-data-message">
                    <p>{{ content[language].noData || 'No data available' }}</p>
                  </div>
                  
                  <div v-else>
                    <div v-for="(trait, traitName) in interpretationData" :key="traitName" class="trait-section">
                      <div class="trait-header" @click="toggleTrait(`int_${traitName}`)">
                        <h4 class="trait-title">{{ traitName }}</h4>
                        <div class="collapse-icon" :class="{ 'expanded': expandedTraits[`int_${traitName}`] }">
                          <i class="icon">▼</i>
                        </div>
                      </div>
                      
                      <div class="trait-content" v-show="expandedTraits[`int_${traitName}`]">
                        <div class="table-responsive">
                          <table class="table table-striped">
                            <thead>
                              <tr>
                                <th>{{ content[language].interpretationTable.table.score }}</th>
                                <th>{{ content[language].interpretationTable.table.indicator }}</th>
                                <th>{{ content[language].interpretationTable.table.defense }}</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(value, score) in trait" :key="score">
                                <td><strong>{{ score }}</strong></td>
                                <td>{{ value.indicator }}</td>
                                <td>{{ value.defense }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="no-data-message">
                  <p>Interpretation Table data not available</p>
                </div>
              </div>

              <!-- Research Methodology Tab -->
              <div v-if="activeTab === 2" class="tab-pane" style="display: block !important;">
                <div class="methodology-section" v-if="content[language] && content[language].methodology">
                  <h3>{{ content[language].methodology.title }}</h3>
                  <p>{{ content[language].methodology.description }}</p>
                  <div v-if="content[language].methodology.steps" class="row mt-3">
                    <div class="col-md-6" v-for="step in content[language].methodology.steps" :key="step.title">
                      <div class="card h-100">
                        <div class="card-body">
                          <h5 class="card-title">{{ step.title }}</h5>
                          <p class="card-text">{{ step.description }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="no-data-message">
                  <p>Methodology data not available</p>
                </div>
              </div>

              <!-- Key Elements Tab -->
              <div v-if="activeTab === 3" class="tab-pane" style="display: block !important;">
                <div class="features-section" v-if="content[language] && content[language].features">
                  <h3>{{ content[language].features.title }}</h3>
                  <ul v-if="content[language].features.list">
                    <li v-for="feature in content[language].features.list" :key="feature">{{ feature }}</li>
                  </ul>
                </div>
                <div v-else class="no-data-message">
                  <p>Features data not available</p>
                </div>
              </div>

              <!-- Raw Data Repository Tab -->
              <div v-if="activeTab === 4" class="tab-pane" style="display: block !important;">
                <div class="raw-data-section" v-if="content[language] && content[language].rawData">
                  <h3>{{ content[language].rawData.title }}</h3>
                  <p>{{ content[language].rawData.description }}</p>
                  <a :href="content[language].rawData.link" target="_blank" class="btn btn-primary btn-lg custom-apt-btn">
                    {{ content[language].rawData.button }}
                  </a>
                </div>
                <div v-else class="no-data-message">
                  <p>Raw data information not available</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DataService from '../services/DataServices'
import { languageStore } from '../stores/languageStore'

export default {
  name: 'Base',
  data() {
    return {
      loading: true,
      activeTab: 0,
      codificationData: {},
      interpretationData: {},
      expandedTraits: {},
      content: {
        en: {
          title: 'Base',
          loading: 'Loading base data...',
          codificationMatrix: {
            title: 'Codification Matrix',
            description: 'Detailed behavioral descriptions and TTP indicators for each trait level.',
            table: {
              score: 'Score',
              level: 'Level',
              behavioral: 'Behavioral Description',
              ttp: 'TTP Indicators'
            }
          },
          interpretationTable: {
            title: 'Interpretation Table',
            description: 'Trait indicators and corresponding defense strategies for each score level.',
            table: {
              score: 'Score',
              indicator: 'Indicator',
              defense: 'Defense Strategy'
            }
          },
          methodology: {
            title: 'Research Methodology',
            description: 'Our approach combines criminological theories (Differential Association, Rational Choice, Routine Activities, Neutralization) with psychological personality assessment to analyze APT group behavior.',
            steps: [
              {
                title: 'Criminological Analysis',
                description: 'Apply classical theories to understand APT motivation, learning patterns, and decision-making processes in cyber environments.'
              },
              {
                title: 'Personality Profiling',
                description: 'Use the Big Five model (OCEAN) to assess organizational traits of APT groups as unified entities.'
              },
              {
                title: 'Behavioral Modeling',
                description: 'Create predictive frameworks that link theoretical insights to practical threat assessment and response strategies.'
              },
              {
                title: 'Case Study Validation',
                description: 'Test the model with hypothetical APT scenarios to validate the approach and refine predictive capabilities.'
              }
            ]
          },
          features: {
            title: 'Key Elements',
            list: [
              'Criminological theory integration for APT analysis',
              'Big Five & Dark Tetrad personality assessment for threat groups',
              'Predictive behavioral modeling capabilities',
              'Interactive APT profiling and scoring',
              'Multi-language support (English and Spanish)',
              'Practical incident response recommendations'
            ]
          },
          rawData: {
            title: 'Raw Data Repository',
            description: 'Access the complete dataset, JSON files, used in this research project. The repository contains all APT group profiles, cluster analysis data, and supporting materials.',
            link: 'https://github.com/Base4Security/PRISM/',
            button: 'View Raw Data'
          },
          noData: 'No data available. Please check the console for errors.'
        },
        es: {
          title: 'Base',
          loading: 'Cargando datos base...',
          codificationMatrix: {
            title: 'Matriz de Codificación',
            description: 'Descripciones conductuales detalladas e indicadores TTP para cada nivel de rasgo.',
            table: {
              score: 'Puntaje',
              level: 'Nivel',
              behavioral: 'Descripción Conductual',
              ttp: 'Indicadores TTP'
            }
          },
          interpretationTable: {
            title: 'Tabla de Interpretación',
            description: 'Indicadores de rasgos y estrategias de defensa correspondientes para cada nivel de puntaje.',
            table: {
              score: 'Puntaje',
              indicator: 'Indicador',
              defense: 'Estrategia de Defensa'
            }
          },
          methodology: {
            title: 'Metodología de Investigación',
            description: 'Nuestro enfoque combina teorías criminológicas (Asociación Diferencial, Elección Racional, Actividades Cotidianas, Neutralización) con evaluación psicológica de personalidad para analizar el comportamiento de grupos APT.',
            steps: [
              {
                title: 'Análisis Criminológico',
                description: 'Aplicar teorías clásicas para entender la motivación, patrones de aprendizaje y procesos de toma de decisiones de APTs en entornos cibernéticos.'
              },
              {
                title: 'Perfilado de Personalidad',
                description: 'Usar el modelo de los Cinco Grandes (OCEAN) para evaluar rasgos organizacionales de grupos APT como entidades unificadas.'
              },
              {
                title: 'Modelado Conductual',
                description: 'Crear marcos predictivos que vinculen insights teóricos con estrategias prácticas de evaluación y respuesta a amenazas.'
              },
              {
                title: 'Validación con Casos de Estudio',
                description: 'Probar el modelo con escenarios APT hipotéticos para validar el enfoque y refinar capacidades predictivas.'
              }
            ]
          },
          features: {
            title: 'Elementos Principales',
            list: [
              'Integración de teorías criminológicas para análisis APT',
              'Evaluación de personalidad Big Five & Dark Tetrad para grupos de amenazas',
              'Capacidades de modelado conductual predictivo',
              'Perfilado y puntuación interactiva de APTs',
              'Soporte multiidioma (Inglés y Español)',
              'Recomendaciones prácticas de respuesta a incidentes'
            ]
          },
          rawData: {
            title: 'Repositorio de Datos Crudos',
            description: 'Acceda al conjunto de datos completo, archivos JSON, utilizados en este proyecto de investigación. El repositorio contiene todos los perfiles de grupos APT, datos de análisis de clusters y materiales de apoyo.',
            link: 'https://github.com/Base4Security/PRISM/tree/main',
            button: 'Ver Datos Crudos'
          },
          noData: 'No hay datos disponibles. Por favor, revise la consola para ver errores.'
        }
      }
    }
  },
  computed: {
    language() {
      return languageStore.getLanguage()
    },
    currentContent() {
      const lang = this.language || 'en'
      return this.content && this.content[lang] ? this.content[lang] : {}
    },
    tabs() {
      if (!this.currentContent) {
        return []
      }
      return [
        { title: this.currentContent.codificationMatrix?.title || 'Codification Matrix' },
        { title: this.currentContent.interpretationTable?.title || 'Interpretation Table' },
        { title: this.currentContent.methodology?.title || 'Research Methodology' },
        { title: this.currentContent.features?.title || 'Key Elements' },
        { title: this.currentContent.rawData?.title || 'Raw Data Repository' }
      ]
    }
  },
  watch: {
    language() {
      this.loadData()
    }
  },
  methods: {
    loadData() {
      this.loading = true
      const currentLang = this.language
      
      const codificationUrl = `https://raw.githubusercontent.com/Base4Security/PRISM/refs/heads/main/base/CodificationMatrix${currentLang.toUpperCase()}.json`
      const interpretationUrl = `https://raw.githubusercontent.com/Base4Security/PRISM/refs/heads/main/base/interpretationTable${currentLang.toUpperCase()}.json`
      
      Promise.all([
        DataService.fetchJSON(codificationUrl),
        DataService.fetchJSON(interpretationUrl)
      ])
          .then(([codification, interpretation]) => {
            if (codification && typeof codification === 'object') {
            this.codificationData = codification
            // Auto-expand first trait by default
            const firstTrait = Object.keys(codification)[0]
            if (firstTrait) {
              this.expandedTraits[`cod_${firstTrait}`] = true
            }
          } else {
            console.warn('Codification data is not valid:', codification)
          }
          if (interpretation && typeof interpretation === 'object') {
            this.interpretationData = interpretation
            // Auto-expand first trait by default if not already expanded
            const firstTrait = Object.keys(interpretation)[0]
            if (firstTrait) {
              this.expandedTraits[`int_${firstTrait}`] = true
            }
          } else {
            console.warn('Interpretation data is not valid:', interpretation)
          }
          this.loading = false
        })
        .catch(error => {
          console.error('Error loading base data:', error)
          this.loading = false
        })
    },
    toggleTrait(traitName) {
      this.expandedTraits[traitName] = !this.expandedTraits[traitName]
    }
  },
  mounted() {
    this.loadData()
  }
}
</script>

<style scoped>
.base-page {
  padding: 20px;
  background: var(--mm-custom-color-1);
  min-height: 100vh;
}

h1 {
  color: var(--mm-custom-color-2);
  font-size: 2rem;
  font-weight: bold;
}

/* Loading styles */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: var(--mm-custom-color-3);
  border-radius: 15px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--mm-custom-color-2);
  min-height: 400px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(220, 120, 15, 0.3);
  border-top: 4px solid #DC780F;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.loading-container p {
  color: var(--mm-custom-color-5);
  margin: 0;
  font-size: 1.1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Tabs */
.tabs-container {
  background: var(--mm-custom-color-3);
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
  border: 2px solid var(--mm-custom-color-2);
}

.nav-tabs {
  border-bottom: 2px solid var(--mm-custom-color-2);
  margin-bottom: 25px;
}

.nav-tabs .nav-item {
  margin-bottom: -2px;
}

.nav-tabs .nav-link {
  color: var(--mm-custom-color-5);
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  padding: 12px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.nav-tabs .nav-link:hover {
  color: var(--mm-custom-color-2);
  background: rgba(220, 120, 15, 0.1);
}

.nav-tabs .nav-link.active {
  color: var(--mm-custom-color-2);
  background: transparent;
  border-bottom-color: var(--mm-custom-color-2);
  font-weight: bold;
}

.tab-content {
  min-height: 400px;
}

.tab-pane {
  padding: 20px 0;
  min-height: 300px;
  display: block;
}

/* Section styles */
.table-section,
.methodology-section,
.features-section,
.raw-data-section {
  margin-bottom: 30px;
}

.section-description {
  color: var(--mm-custom-color-5);
  margin-bottom: 25px;
  font-size: 1rem;
  line-height: 1.6;
}

h3 {
  color: var(--mm-custom-color-2);
  font-size: 1.5rem;
  margin-bottom: 15px;
  font-weight: bold;
}

/* Trait sections */
.trait-section {
  margin-bottom: 20px;
  background: var(--mm-custom-color-1);
  border-radius: 10px;
  border: 1px solid rgba(220, 120, 15, 0.2);
  overflow: hidden;
}

.trait-header {
  padding: 15px 20px;
  background: rgba(220, 120, 15, 0.1);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
}

.trait-header:hover {
  background: rgba(220, 120, 15, 0.2);
}

.trait-title {
  color: var(--mm-custom-color-2);
  font-size: 1.2rem;
  margin: 0;
  font-weight: bold;
}

.collapse-icon {
  color: var(--mm-custom-color-2);
  font-size: 1rem;
  transition: transform 0.3s ease;
}

.collapse-icon.expanded {
  transform: rotate(180deg);
}

.trait-content {
  padding: 20px;
}

/* Table styles */
.table-responsive {
  overflow-x: auto;
}

.table {
  background: var(--mm-custom-color-3);
  color: var(--mm-custom-color-5);
  margin-bottom: 0;
}

.table thead th {
  background: var(--mm-custom-color-2);
  color: var(--mm-custom-color-5);
  border: none;
  padding: 12px;
  font-weight: bold;
  text-align: left;
}

.table tbody td {
  padding: 12px;
  border-top: 1px solid rgba(220, 120, 15, 0.2);
  vertical-align: top;
}

.table tbody tr:hover {
  background: rgba(220, 120, 15, 0.1);
}

.table tbody tr:first-child td {
  border-top: none;
}

/* Methodology cards */
.card {
  background: var(--mm-custom-color-1);
  border: 1px solid rgba(220, 120, 15, 0.2);
  border-radius: 10px;
  margin-bottom: 20px;
}

.card-title {
  color: var(--mm-custom-color-2);
  font-size: 1.1rem;
  font-weight: bold;
}

.card-text {
  color: var(--mm-custom-color-5);
  line-height: 1.6;
}

/* Features list */
.features-section ul {
  list-style: none;
  padding: 0;
}

.features-section li {
  color: var(--mm-custom-color-5);
  padding: 10px 0;
  padding-left: 25px;
  position: relative;
  line-height: 1.6;
}

.features-section li::before {
  content: "•";
  color: var(--mm-custom-color-2);
  font-weight: bold;
  position: absolute;
  left: 0;
  font-size: 1.5rem;
}

/* Raw data section */
.raw-data-section {
  text-align: center;
  padding: 40px 20px;
}

.raw-data-section p {
  color: var(--mm-custom-color-5);
  margin-bottom: 30px;
  font-size: 1.1rem;
  line-height: 1.6;
}

.custom-apt-btn {
  background-color: transparent !important;
  border: 2px solid #DC780F !important;
  color: #DC780F !important;
  transition: all 0.3s ease;
  padding: 12px 30px;
  font-size: 1rem;
}

.custom-apt-btn:hover {
  background-color: #DC780F !important;
  color: white !important;
}

/* No data message */
.no-data-message {
  text-align: center;
  padding: 40px 20px;
  background: var(--mm-custom-color-1);
  border-radius: 10px;
  border: 1px solid rgba(220, 120, 15, 0.2);
  margin: 20px 0;
}

.no-data-message p {
  color: var(--mm-custom-color-5);
  font-size: 1.1rem;
  margin: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .base-page {
    padding: 15px;
  }
  
  .tabs-container {
    padding: 15px;
  }
  
  .nav-tabs .nav-link {
    padding: 10px 15px;
    font-size: 0.9rem;
  }
  
  .table-responsive {
    font-size: 0.85rem;
  }
  
  .table thead th,
  .table tbody td {
    padding: 8px;
  }
}
</style>

